import { CollapsibleBox } from '@/components/common/collapsible-box.tsx';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { GroupsOutlined } from '@mui/icons-material';
import { useMemo } from 'react';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { ContributorInvitationIcon } from '@/components/events/detail/tabs/contributors/contributor-invitation-icon.tsx';
import { ContributorInvitationExpandButton } from '@/components/events/detail/tabs/contributors/contributor-invitation-expand-button.tsx';
import { CollapsibleGroup } from '@/components/common/collapsible-group';
import { useSearchParams } from 'react-router';
import { useTranslations } from '@/i18n/use-translations';
import type { CrewWidgetRow } from '@/utils/contributor-extraction.utils';
import { CrewDeleteHeaderButton } from './crew-delete-header-button';
import UpdateContributorForm from '@/components/events/detail/tabs/contributors/update-contributor.form';
import { ContributorInviteStatusPanel } from '@/components/events/detail/tabs/contributors/contributor-invite-status-panel.tsx';
import { useEventStore } from '@/state/stores/use-event-store';
import { getFlowModeratorPublicationHints, resolvePlanFlowDto } from '@/utils/contributor-invite-publication.utils';

export type CrewListProps = {
  rows: CrewWidgetRow[];
  flow?: FlowDto;
  eventSlotId?: string;
  sx?: SxProps<Theme>;
};

export const CrewList = ({ rows, flow, eventSlotId: eventSlotIdProp, sx }: CrewListProps) => {
  const [searchParams] = useSearchParams();
  const t = useTranslations();

  const eventSlotIdParam = (eventSlotIdProp ?? searchParams.get('eventSlotId')) || undefined;
  const forceMasterMode = !eventSlotIdParam;

  const eventPlan = useEventStore((s) => s.eventPlan);
  const eventFlowsPlan = useEventStore((s) => s.eventFlowsPlan);
  const eventFlowsOrganize = useEventStore((s) => s.eventFlowsOrganize);
  const planFlowForCrewInvites = useMemo(
    () => (flow ? resolvePlanFlowDto(flow, eventFlowsPlan, eventFlowsOrganize) : undefined),
    [flow, eventFlowsPlan, eventFlowsOrganize],
  );
  const flowCrewPublicationHints = useMemo(
    () =>
      getFlowModeratorPublicationHints({
        planFlow: planFlowForCrewInvites,
        eventPlan,
        eventSlotId: eventSlotIdParam,
        eventFlowsOrganize,
      }),
    [planFlowForCrewInvites, eventPlan, eventSlotIdParam, eventFlowsOrganize],
  );

  const normalizeId = (value?: unknown) => {
    if (!value) return undefined;
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      if ('id' in value && typeof (value as { id?: string }).id === 'string') {
        return (value as { id: string }).id;
      }
      if ('_id' in value && typeof (value as { _id?: string })._id === 'string') {
        return (value as { _id: string })._id;
      }
      if ('$oid' in value && typeof (value as { $oid?: string }).$oid === 'string') {
        return (value as { $oid: string }).$oid;
      }
    }
    return undefined;
  };

  const getRoleSlotForEventSlot = (
    entityType: CrewWidgetRow['entityType'],
    role: CrewWidgetRow['role'],
    eventSlotId: string | undefined,
  ) => {
    if (!eventSlotId) return undefined;
    if (entityType === 'Flow') {
      return role.slots?.find(
        (slot) => slot.entityType === 'Flow' && normalizeId(slot.eventSlotId) === eventSlotId && slot.deleted !== true,
      );
    }
    return role.slots?.find(
      (slot) =>
        slot.deleted !== true &&
        (normalizeId(slot.eventSlotId) === eventSlotId ||
          (slot.entityType === 'EventSlot' && normalizeId(slot.entity) === eventSlotId)),
    );
  };

  const flowDetailsInviteForRow = (row: CrewWidgetRow) => {
    const { role, entityType } = row;
    if (eventSlotIdParam) {
      const roleSlotForEventSlot = getRoleSlotForEventSlot(entityType, role, eventSlotIdParam);
      const roleHasNoSlots = !role.slots?.some((s) => s.deleted !== true);
      return {
        roleAccepted: Boolean(role.acceptedAt),
        roleInvited: Boolean(role.invitedAt),
        roleStagedForInvite: role.stagedForInvite === true,
        slotInvited: Boolean(roleSlotForEventSlot?.invitedAt),
        slotAccepted: roleHasNoSlots || Boolean(roleSlotForEventSlot?.acceptedAt),
        slotStagedForInvite: roleSlotForEventSlot?.stagedForInvite === true,
      };
    }
    return {
      roleAccepted: Boolean(role.acceptedAt),
      roleInvited: Boolean(role.invitedAt),
      roleStagedForInvite: role.stagedForInvite === true,
      slotInvited: false,
      slotAccepted: true,
      slotStagedForInvite: false,
    };
  };

  return (
    <>
      {rows.length === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, px: 1, py: 2 }}>
          <GroupsOutlined sx={{ fontSize: 28, color: 'action.disabled' }} />
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {t('event.detail.crewWidget.empty')}
          </Typography>
        </Box>
      )}
      <CollapsibleGroup>
        {rows.map((row) => {
          const { contributor, role, entityType, entityId } = row;
          const roleLabelKey = `common.roleLabels.${role.role}` as 'common.roleLabels.owner';
          const subtitle = t(roleLabelKey);
          const roleSlotForEventSlot = getRoleSlotForEventSlot(entityType, role, eventSlotIdParam);
          return (
            <CollapsibleBox
              sx={{ m: '0.4rem 0', ...sx }}
              key={`${contributor.id}-${role.id}`}
              id={`crew-${contributor.id}-${role.id}`}
              title={`${contributor.profiles[0]?.displayName || t('event.detail.contributors.unknownContributor')} · ${subtitle}`}
              iconStart={
                <ContributorInvitationExpandButton itemId={`crew-${contributor.id}-${role.id}`}>
                  <ContributorInvitationIcon
                    contributor={contributor}
                    flowDetailsInvite={flowDetailsInviteForRow(row)}
                  />
                </ContributorInvitationExpandButton>
              }
              iconsEnd={[
                <CrewDeleteHeaderButton
                  key="delete-crew"
                  entityType={entityType}
                  entityId={entityId}
                  contributor={contributor}
                  roleId={role.id}
                  flow={entityType === 'Flow' ? flow : undefined}
                  eventSlotIdParam={eventSlotIdParam}
                  forceMasterMode={forceMasterMode}
                />,
              ]}
              showCollapseArrow
              hideCollapseExpandTooltip
            >
              <UpdateContributorForm
                contributor={contributor}
                entityId={entityId}
                entityType={entityType === 'Flow' ? 'Flow' : 'Event'}
                forceMasterMode={forceMasterMode}
                eventSlotIdOverride={eventSlotIdParam}
                suppressWidgetIconButtons
                hideDeleteButton
                showEmailField
                fieldsLayout="nameEmail"
                inviteStatusPanel={
                  <ContributorInviteStatusPanel
                    key={`crew-${contributor.id}-${role.id}-${roleSlotForEventSlot?.id ?? 'no-slot'}`}
                    contributor={contributor}
                    roleId={role.id}
                    roleInvited={Boolean(role.invitedAt)}
                    roleAccepted={Boolean(role.acceptedAt)}
                    publicationBlockContribution={entityType === 'Flow' ? flowCrewPublicationHints.contribution : null}
                    publicationBlockTimeSlot={entityType === 'Flow' ? flowCrewPublicationHints.timeSlot : null}
                    timeSlot={
                      eventSlotIdParam && roleSlotForEventSlot
                        ? {
                            roleSlotId: roleSlotForEventSlot.id,
                            invited: Boolean(roleSlotForEventSlot.invitedAt),
                            accepted: Boolean(roleSlotForEventSlot.acceptedAt),
                          }
                        : null
                    }
                  />
                }
              />
            </CollapsibleBox>
          );
        })}
      </CollapsibleGroup>
    </>
  );
};
